import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import EditNoteIcon from '@mui/icons-material/EditNote';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import CheckIcon from '@mui/icons-material/Check';
import OButton from '../../../UI/FormControls/OButton';
import { colors } from '../../../../../shared/colors';
import { icons } from '../../../../../shared/icons';
import {
  generisiWorkReportPDF,
  iznosOpreme,
  iznosTehnicara,
  novaOpremaWorkReporta,
  noviRadWorkReporta,
  noviTehnicarWorkReporta,
  prevodWorkReporta,
  prikazDana,
  ukupnoWorkReporta,
} from './generisiWorkReportPDF';

const SVETLO_NARANDZASTA = '#fff4ea';
const KASNJENJE_PREGLEDA = 600;

const kopija = (vrednost) => JSON.parse(JSON.stringify(vrednost));
const naslovKartice = (vrednost) =>
  vrednost.charAt(0) + vrednost.slice(1).toLowerCase();

const narandzastiSwitch = {
  '& .MuiSwitch-switchBase.Mui-checked': { color: colors.ORANGE },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
    backgroundColor: colors.ORANGE,
  },
};

const Prekidac = ({ checked, onChange, labela }) => (
  <FormControlLabel
    sx={{ mr: 0 }}
    control={
      <Switch
        size="small"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        sx={narandzastiSwitch}
      />
    }
    label={<Typography sx={{ fontSize: 13 }}>{labela}</Typography>}
  />
);

const Kartica = ({ redniBroj, naslov, desno, children }) => (
  <Paper
    variant="outlined"
    sx={{ borderRadius: 2, mb: 2, overflow: 'hidden', borderColor: '#e6e6e6' }}
  >
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 1,
        px: 2,
        py: 1,
        borderBottom: '1px solid #eee',
        backgroundColor: '#fafafa',
      }}
    >
      <Box
        sx={{
          width: 24,
          height: 24,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 12,
          fontWeight: 600,
          color: colors.WHITE,
          backgroundColor: colors.ORANGE,
        }}
      >
        {redniBroj}
      </Box>
      <Typography sx={{ fontWeight: 600, fontSize: 15, flex: 1 }}>
        {naslov}
      </Typography>
      {desno}
    </Box>
    {children}
  </Paper>
);

const DodajRed = ({ onClick, tekst }) => (
  <Button
    size="small"
    startIcon={<AddIcon />}
    onClick={onClick}
    sx={{ color: colors.ORANGE, textTransform: 'none' }}
  >
    {tekst}
  </Button>
);

const Prazno = ({ tekst }) => (
  <Typography sx={{ fontSize: 13, color: 'text.secondary', p: 2 }}>
    {tekst}
  </Typography>
);

const Celija = ({ kolona, vrednost, onChange }) => (
  <TextField
    fullWidth
    size="small"
    variant="standard"
    value={vrednost ?? ''}
    type={kolona.broj ? 'number' : 'text'}
    onChange={(e) => onChange(e.target.value)}
    InputProps={{
      inputProps: {
        step: 'any',
        min: kolona.broj ? 0 : undefined,
        style: { fontSize: 13, textAlign: kolona.broj ? 'right' : 'left' },
      },
      endAdornment: kolona.iznos ? (
        <InputAdornment position="end">€</InputAdornment>
      ) : undefined,
    }}
  />
);

const DugmeObrisi = ({ tekst, onClick }) => (
  <Tooltip title={tekst}>
    <IconButton
      size="small"
      onClick={onClick}
      sx={{ '&:hover': { color: colors.DELETE } }}
    >
      <DeleteOutlineIcon fontSize="small" />
    </IconButton>
  </Tooltip>
);

const RedTabele = memo(
  ({ sekcija, red, kolone, obrisiTekst, onCelija, onObrisi }) => (
    <TableRow hover>
      {kolone.map((kolona) => (
        <TableCell
          key={kolona.kljuc}
          align={kolona.racun ? 'right' : 'left'}
          sx={{ py: 0.5, fontSize: 13 }}
        >
          {kolona.racun ? (
            kolona.racun(red)
          ) : (
            <Celija
              kolona={kolona}
              vrednost={red[kolona.kljuc]}
              onChange={(vrednost) =>
                onCelija(sekcija, red.kljucReda, kolona.kljuc, vrednost)
              }
            />
          )}
        </TableCell>
      ))}
      <TableCell sx={{ py: 0.5 }}>
        <DugmeObrisi
          tekst={obrisiTekst}
          onClick={() => onObrisi(sekcija, red.kljucReda)}
        />
      </TableCell>
    </TableRow>
  ),
);

const TabelaRedova = ({
  sekcija,
  redovi,
  kolone,
  p,
  t,
  zbir,
  onCelija,
  onObrisi,
}) =>
  redovi.length ? (
    <Box sx={{ overflowX: 'auto' }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            {kolone.map((kolona) => (
              <TableCell
                key={kolona.kljuc}
                align={kolona.broj || kolona.racun ? 'right' : 'left'}
                sx={{
                  minWidth: kolona.sirina,
                  fontWeight: 600,
                  fontSize: 12,
                  color: '#555',
                }}
              >
                {kolona.labela}
              </TableCell>
            ))}
            <TableCell sx={{ width: 48 }} />
          </TableRow>
        </TableHead>
        <TableBody>
          {redovi.map((red) => (
            <RedTabele
              key={red.kljucReda}
              sekcija={sekcija}
              red={red}
              kolone={kolone}
              obrisiTekst={t('pdfDialog.obrisiRed')}
              onCelija={onCelija}
              onObrisi={onObrisi}
            />
          ))}
          {zbir && (
            <TableRow sx={{ backgroundColor: SVETLO_NARANDZASTA }}>
              {kolone.map((kolona, index) => (
                <TableCell
                  key={kolona.kljuc}
                  align={kolona.broj || kolona.racun ? 'right' : 'left'}
                  sx={{ fontWeight: 600, fontSize: 13 }}
                >
                  {index === 0 ? p.ukupno : zbir[kolona.kljuc]}
                </TableCell>
              ))}
              <TableCell />
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Box>
  ) : (
    <Prazno tekst={t('pdfDialog.nemaStavki')} />
  );

const RadIzmene = memo(({ red, p, obrisiTekst, onCelija, onObrisi }) => (
  <Paper
    variant="outlined"
    sx={{ p: 1.5, borderRadius: 2, borderColor: '#eee' }}
  >
    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mb: 1.5 }}>
      <TextField
        size="small"
        label={p.datum}
        value={red.datum}
        onChange={(e) =>
          onCelija('radovi', red.kljucReda, 'datum', e.target.value)
        }
        sx={{ width: 140 }}
      />
      <TextField
        size="small"
        label={p.tehnicar}
        value={red.tehnicar}
        onChange={(e) =>
          onCelija('radovi', red.kljucReda, 'tehnicar', e.target.value)
        }
        sx={{ flex: 1 }}
      />
      <DugmeObrisi
        tekst={obrisiTekst}
        onClick={() => onObrisi('radovi', red.kljucReda)}
      />
    </Box>
    <TextField
      fullWidth
      multiline
      minRows={4}
      maxRows={16}
      size="small"
      value={red.tekst}
      onChange={(e) =>
        onCelija('radovi', red.kljucReda, 'tekst', e.target.value)
      }
    />
  </Paper>
));

const NOVI_RED = {
  radovi: noviRadWorkReporta,
  oprema: novaOpremaWorkReporta,
  tehnicari: noviTehnicarWorkReporta,
};

const IzmenaWorkReportaDialog = ({ izvestaj, jezik, onClose, onPrimeni }) => {
  const { t } = useTranslation();
  const [model, setModel] = useState(null);
  const [pregledUrl, setPregledUrl] = useState(null);
  const [osvezava, setOsvezava] = useState(false);
  const urlRef = useRef(null);
  const p = useMemo(() => prevodWorkReporta(jezik), [jezik]);

  useEffect(() => {
    setModel(izvestaj ? kopija(izvestaj) : null);
  }, [izvestaj]);

  // Pregled se generiše istom funkcijom kao PDF koji se preuzima/šalje
  useEffect(() => {
    if (!model) return undefined;
    let aktivan = true;
    setOsvezava(true);
    const tajmer = setTimeout(async () => {
      try {
        const { doc } = await generisiWorkReportPDF({ izvestaj: model, jezik });
        if (!aktivan) return;
        const url = URL.createObjectURL(doc.output('blob'));
        if (urlRef.current) URL.revokeObjectURL(urlRef.current);
        urlRef.current = url;
        setPregledUrl(url);
      } catch (error) {
        console.error(error);
      } finally {
        if (aktivan) setOsvezava(false);
      }
    }, KASNJENJE_PREGLEDA);
    return () => {
      aktivan = false;
      clearTimeout(tajmer);
    };
  }, [model, jezik]);

  useEffect(
    () => () => {
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    },
    [],
  );

  const izmeni = useCallback(
    (kljuc, vrednost) => setModel((m) => ({ ...m, [kljuc]: vrednost })),
    [],
  );
  const izmeniCeliju = useCallback(
    (sekcija, kljucReda, polje, vrednost) =>
      setModel((m) => ({
        ...m,
        [sekcija]: m[sekcija].map((red) =>
          red.kljucReda === kljucReda ? { ...red, [polje]: vrednost } : red,
        ),
      })),
    [],
  );
  const obrisiRed = useCallback(
    (sekcija, kljucReda) =>
      setModel((m) => ({
        ...m,
        [sekcija]: m[sekcija].filter((red) => red.kljucReda !== kljucReda),
      })),
    [],
  );
  const dodajRed = (sekcija, izvor) =>
    setModel((m) => ({
      ...m,
      [sekcija]: [...m[sekcija], NOVI_RED[sekcija](izvor)],
    }));

  if (!model) return null;

  const cene = model.dozvoljeneCene && model.prikaziCene;
  const ukupno = ukupnoWorkReporta(model);
  const zbirKolone = (sekcija, kljuc) =>
    model[sekcija].reduce((acc, red) => acc + (Number(red[kljuc]) || 0), 0);
  const obrisiTekst = t('pdfDialog.obrisiRed');

  const koloneOpreme = [
    { kljuc: 'naziv', labela: p.naziv, sirina: 220 },
    { kljuc: 'kolicina', labela: p.kolicina, sirina: 90, broj: true },
    ...(cene
      ? [
          {
            kljuc: 'cena',
            labela: p.cena,
            sirina: 120,
            broj: true,
            iznos: true,
          },
          {
            kljuc: 'iznos',
            labela: p.iznos,
            sirina: 110,
            racun: (red) => p.formatBroj(iznosOpreme(red)),
          },
        ]
      : []),
  ];
  const koloneTehnicara = [
    { kljuc: 'tehnicar', labela: p.tehnicar, sirina: 180 },
    { kljuc: 'sati', labela: p.sati, sirina: 100, broj: true },
    { kljuc: 'satiPuta', labela: p.satiPuta, sirina: 110, broj: true },
    ...(cene
      ? [
          {
            kljuc: 'cenaSata',
            labela: p.cenaSata,
            sirina: 120,
            broj: true,
            iznos: true,
          },
          {
            kljuc: 'cenaPuta',
            labela: p.cenaPuta,
            sirina: 120,
            broj: true,
            iznos: true,
          },
          {
            kljuc: 'iznos',
            labela: p.iznos,
            sirina: 110,
            racun: (red) => p.formatBroj(iznosTehnicara(red)),
          },
        ]
      : []),
  ];

  return (
    <Dialog
      open
      onClose={onClose}
      fullWidth
      maxWidth={false}
      PaperProps={{ sx: { borderRadius: 3, height: '94vh', width: '96vw' } }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1.5,
          pr: 7,
        }}
      >
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: SVETLO_NARANDZASTA,
            color: colors.ORANGE,
          }}
        >
          <EditNoteIcon />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ fontWeight: 600, fontSize: 18 }}>
            {t('pdfDialog.workReport')}
            {model.broj ? ` – ${model.broj}` : ''}
            {` (${prikazDana(model.datum)})`}
          </Typography>
          <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
            {t('pdfDialog.izmenaWorkReportaOpis')}
          </Typography>
        </Box>
        {model.dozvoljeneCene && (
          <Prekidac
            checked={model.prikaziCene}
            onChange={(vrednost) => izmeni('prikaziCene', vrednost)}
            labela={t('pdfDialog.prikaziCene')}
          />
        )}
        {cene && (
          <Chip
            label={`${p.ukupno}: ${p.formatBroj(ukupno.ukupno)}`}
            sx={{
              fontWeight: 600,
              color: colors.ORANGE,
              backgroundColor: SVETLO_NARANDZASTA,
            }}
          />
        )}
        <IconButton
          onClick={onClose}
          sx={{ position: 'absolute', right: 12, top: 12 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          p: 0,
          borderColor: '#eee',
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            lg: 'minmax(0, 1fr) minmax(0, 1fr)',
          },
          overflow: { xs: 'auto', lg: 'hidden' },
        }}
      >
        <Box
          sx={{
            p: 2,
            backgroundColor: '#f6f6f6',
            overflowY: { lg: 'auto' },
          }}
        >
          <Kartica redniBroj={1} naslov={t('pdfDialog.zaglavlje')}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                gap: 2,
                p: 2,
              }}
            >
              <TextField
                size="small"
                label={p.broj}
                value={model.broj}
                disabled
              />
              <TextField
                size="small"
                label={p.datum}
                value={prikazDana(model.datum)}
                disabled
                helperText={t('pdfDialog.datumIzPopupa')}
              />
              <TextField
                size="small"
                label={p.kupac}
                value={model.kupac}
                onChange={(e) => izmeni('kupac', e.target.value)}
              />
              <TextField
                size="small"
                label={t('pdfDialog.adresaKupca')}
                value={model.adresaKupca}
                onChange={(e) => izmeni('adresaKupca', e.target.value)}
              />
              <TextField
                size="small"
                label={p.robot}
                value={model.robot}
                onChange={(e) => izmeni('robot', e.target.value)}
              />
              <TextField
                size="small"
                label={p.trazio}
                value={model.trazio}
                onChange={(e) => izmeni('trazio', e.target.value)}
              />
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Prekidac
                  checked={Boolean(model.garancija)}
                  onChange={(vrednost) => izmeni('garancija', vrednost)}
                  labela={`${naslovKartice(p.garancija)}: ${p.daNe(
                    model.garancija,
                  )}`}
                />
              </Box>
            </Box>
          </Kartica>

          <Kartica redniBroj={2} naslov={naslovKartice(p.opisKvara)}>
            <Box sx={{ p: 2 }}>
              <TextField
                fullWidth
                multiline
                minRows={3}
                maxRows={12}
                size="small"
                value={model.opisKvara}
                onChange={(e) => izmeni('opisKvara', e.target.value)}
              />
            </Box>
          </Kartica>

          <Kartica
            redniBroj={3}
            naslov={naslovKartice(p.radovi)}
            desno={
              <DodajRed
                tekst={t('pdfDialog.dodajRed')}
                onClick={() =>
                  dodajRed('radovi', { datum: prikazDana(model.datum) })
                }
              />
            }
          >
            {model.radovi.length ? (
              <Box sx={{ display: 'grid', gap: 1.5, p: 2 }}>
                {model.radovi.map((red) => (
                  <RadIzmene
                    key={red.kljucReda}
                    red={red}
                    p={p}
                    obrisiTekst={obrisiTekst}
                    onCelija={izmeniCeliju}
                    onObrisi={obrisiRed}
                  />
                ))}
              </Box>
            ) : (
              <Prazno tekst={t('pdfDialog.nemaRadova')} />
            )}
          </Kartica>

          <Kartica
            redniBroj={4}
            naslov={naslovKartice(p.oprema)}
            desno={
              <DodajRed
                tekst={t('pdfDialog.dodajRed')}
                onClick={() => dodajRed('oprema')}
              />
            }
          >
            <TabelaRedova
              sekcija="oprema"
              redovi={model.oprema}
              kolone={koloneOpreme}
              p={p}
              t={t}
              zbir={
                cene && model.oprema.length
                  ? { iznos: p.formatBroj(ukupno.delovi) }
                  : null
              }
              onCelija={izmeniCeliju}
              onObrisi={obrisiRed}
            />
          </Kartica>

          <Kartica
            redniBroj={5}
            naslov={t('pdfDialog.satiTehnicara')}
            desno={
              <DodajRed
                tekst={t('pdfDialog.dodajRed')}
                onClick={() => dodajRed('tehnicari')}
              />
            }
          >
            <TabelaRedova
              sekcija="tehnicari"
              redovi={model.tehnicari}
              kolone={koloneTehnicara}
              p={p}
              t={t}
              zbir={
                model.tehnicari.length
                  ? {
                      sati: p.formatSati(zbirKolone('tehnicari', 'sati')),
                      satiPuta: p.formatSati(
                        zbirKolone('tehnicari', 'satiPuta'),
                      ),
                      iznos: p.formatBroj(ukupno.rad + ukupno.putovanje),
                    }
                  : null
              }
              onCelija={izmeniCeliju}
              onObrisi={obrisiRed}
            />
            {cene && (
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  gap: 1,
                  p: 2,
                  borderTop: '1px solid #eee',
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Prekidac
                    checked={model.prikaziZbirCena !== false}
                    onChange={(vrednost) => izmeni('prikaziZbirCena', vrednost)}
                    labela={t('pdfDialog.prikaziZbirCena')}
                  />
                </Box>
                {model.prikaziZbirCena !== false &&
                  [
                    [
                      `${p.rad} (${p.formatSati(ukupno.satiRada)} h)`,
                      ukupno.rad,
                    ],
                    [
                      `${p.putovanje} (${p.formatSati(ukupno.satiPuta)} h)`,
                      ukupno.putovanje,
                    ],
                    [p.delovi, ukupno.delovi],
                  ].map(([labela, iznos]) => (
                    <Chip
                      key={labela}
                      variant="outlined"
                      label={`${labela}: ${p.formatBroj(iznos)}`}
                    />
                  ))}
                {model.prikaziZbirCena !== false && (
                  <Chip
                    label={`${p.ukupno}: ${p.formatBroj(ukupno.ukupno)}`}
                    sx={{
                      fontWeight: 600,
                      color: colors.WHITE,
                      backgroundColor: colors.ORANGE,
                    }}
                  />
                )}
              </Box>
            )}
          </Kartica>

          <Kartica redniBroj={6} naslov={naslovKartice(p.napomena)}>
            <Box sx={{ p: 2 }}>
              <TextField
                fullWidth
                multiline
                minRows={3}
                maxRows={12}
                size="small"
                value={model.napomena}
                placeholder={t('pdfDialog.dodatnaNapomenaPlaceholder')}
                onChange={(e) => izmeni('napomena', e.target.value)}
              />
            </Box>
          </Kartica>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            minHeight: { xs: '80vh', lg: 0 },
            borderLeft: { lg: '1px solid #eee' },
            backgroundColor: '#525659',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 2,
              py: 1,
              backgroundColor: colors.WHITE,
              borderBottom: '1px solid #eee',
            }}
          >
            <VisibilityOutlinedIcon
              fontSize="small"
              sx={{ color: colors.ORANGE }}
            />
            <Typography sx={{ fontWeight: 600, fontSize: 14, flex: 1 }}>
              {t('pdfDialog.pregledUzivo')}
            </Typography>
            {osvezava && (
              <>
                <CircularProgress size={14} sx={{ color: colors.ORANGE }} />
                <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                  {t('pdfDialog.osvezavanje')}
                </Typography>
              </>
            )}
          </Box>
          {pregledUrl ? (
            <iframe
              title={t('pdfDialog.pregledUzivo')}
              src={`${pregledUrl}#toolbar=0&navpanes=0&view=FitH`}
              style={{ flex: 1, width: '100%', border: 'none' }}
            />
          ) : (
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CircularProgress sx={{ color: colors.WHITE }} />
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <OButton
          text={t('pdfDialog.otkazi')}
          startIcon={icons.DIALOG_CLOSE}
          onClick={onClose}
          style={{
            color: colors.ORANGE,
            backgroundColor: colors.WHITE,
            border: `1px solid ${colors.ORANGE}`,
          }}
        />
        <OButton
          text={t('pdfDialog.primeniIzmene')}
          startIcon={<CheckIcon htmlColor={colors.WHITE} />}
          onClick={() => onPrimeni(model)}
          style={{
            color: colors.WHITE,
            backgroundColor: colors.ORANGE,
            border: `1px solid ${colors.ORANGE}`,
          }}
        />
      </DialogActions>
    </Dialog>
  );
};

export default IzmenaWorkReportaDialog;
